import React from "react";
import * as R from "ramda";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import styled from "styled-components/macro";

const SentimentalContainer = styled.section`
  display: flex;
  flex-wrap: wrap;
  padding: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    flex-wrap: nowrap;
  }
`;

const ChartContainer = styled.div`
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const SentimentalList = styled.ul`
  display: block;
  flex: 1;
`;

const SentimentalItem = styled.li`
  padding: 10px 15px;
  box-shadow: 0px 2px 4px 1px #e6e6e6;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  transition: 0.3s ease;
  color: ${props => props.isActive && "#76d701"};
  &:not(:last-of-type) {
    margin-bottom: 20px;
  }
  &:before {
    position: absolute;
    left: 0;
    top: 0;
    content: "";
    display: inline-flex;
    width: 10px;
    height: 100%;
    background: ${props => EmojiBackground[props.type]};
  }
  &:hover {
    box-shadow: 0px 8px 15px -3px gainsboro;
  }
`;

const EmojiBackground = {
  neutral: "#00c6ea",
  happy: "#00ec8e",
  sad: "#e5ec00",
  fear: "#FF9F19",
  angry: "#E83617",
  surprise: "#bdbdbd",
  disgust: "#ab18e8"
};

const EmojiContainer = styled.div`
  margin: 0 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
`;

const SentimentalState = styled.span`
  flex: 1;
`;

const Percentage = styled.span`
  margin-left: 15px;
`;

const Eye = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: white;
  overflow: hidden;
  position: relative;
  ${SentimentalItem}:hover &,
  &.isActive {
    &:first-of-type {
      ${props =>
        props.type === "surprise" &&
        `
          transform: scale(0.75);
          animation: none !important;    
      `};
    }
    animation: blinkeye 1s cubic-bezier(0.61, 0.67, 0.49, 1.04) infinite;
  }
  @keyframes blinkeye {
    0% {
      transform: rotateX(0deg);
    }
    50% {
      transform: rotateX(0deg);
    }
    75% {
      transform: rotateX(80deg);
    }
    100% {
      transform: rotateX(0deg);
    }
  }
  &:after {
    content: "";
    display: inline-flex;
    background: currentColor;
    position: absolute;
    width: 100%;
    height: 5px;
    bottom: 0;
    transform: ${props =>
      props.type === "angry"
        ? "translateY(-8px)"
        : props.type === "happy"
        ? "translateY(2px)"
        : props.type === "sad"
        ? "translateY(3px)"
        : "translateY(5px)"};
    transition: 0.3s 0.5s ease;
    ${SentimentalItem}:hover &,
    &.isActive {
      transform: ${props =>
        props.type === "angry"
          ? "translateY(-5px)"
          : props.type === "happy"
          ? "translateY(2px)"
          : props.type === "sad"
          ? "translateY(2px)"
          : "translateY(5px)"};
    }
  }
  position: absolute;
  top: 10px;
  &:first-of-type {
    left: 5px;
    transition: ${props => props.type === "surprise" && "0.3s ease"};
    transform: ${props => props.type === "surprise" && "scale(0.75)"};
  }
  &:nth-child(2) {
    right: 5px;
  }
`;

const AngryEyeBrow = styled.div`
  display: inline-flex;
  width: 70%;
  height: 2px;
  background: white;
  position: absolute;
  top: 8px;
  left: 5px;
  transition: 0.3s 0.5s ease;
  ${SentimentalItem}:hover &,
  &.isActive {
    transform: translateY(3px);
  }
`;

const Mouth = styled.div`
  display: inline-flex;
  position: absolute;
  background: white;
  top: inherit;
  bottom: ${props => (props.type === "surprise" ? "3px" : "6px")};
  width: ${props => (props.type === "surprise" ? "8px" : "35%")};
  height: ${props => (props.type === "surprise" ? "8px" : "2px")};
  border-radius: ${props => props.type === "surprise" && "50%"};
  left: ${props => (props.type === "surprise" ? "12px" : "10px")};
  transform: ${props =>
    props.type === "surprise" ? "scale(0.7)" : "rotateZ(0deg)"};
  transition: 0.3s ease;
  ${SentimentalItem}:hover &,
  &.isActive {
    transform: ${props =>
      props.type === "angry"
        ? "rotateZ(10deg)"
        : props.type === "neutral"
        ? "rotateZ(0deg)"
        : "surprise"
        ? "scale(1)"
        : "rotateZ(0deg)"};
  }
`;

const EyeBrow = styled.div`
  &:before {
    content: "";
    display: inline-flex;
    width: 30%;
    top: 7px;
    left: 4px;
    position: absolute;
    height: 2px;
    background: white;
    transition: 0.3s ease;
    transform: ${props =>
      props.type === "neutral"
        ? "rotate(0deg)"
        : props.type === "sad"
        ? "rotate(-5deg) translateY(-3px)"
        : "rotate(-5deg)"};
  }
  &:after {
    content: "";
    display: inline-flex;
    width: 30%;
    top: 7px;
    right: 4px;
    position: absolute;
    height: 2px;
    background: white;
    transition: 0.3s ease;
    transform: ${props =>
      props.type === "neutral"
        ? "rotate(0deg)"
        : props.type === "sad"
        ? "rotate(5deg) translateY(-3px)"
        : "rotate(5deg)"};
  }
  top: 0px;
  width: 100%;
  height: 3px;
  position: relative;
  transition: 0.3s 0.5s ease;
  ${SentimentalItem}:hover &,
  &.isActive {
    &:before {
      transform: ${props =>
        props.type === "sad"
          ? " rotate(-8deg) translateY(0px)"
          : props.type === "fear"
          ? "rotate(-8deg) translateY(-3px)"
          : "translateY(-3px)"};
    }
    &:after {
      transform: ${props =>
        props.type === "neutral"
          ? "translateY(-1px)"
          : props.type === "sad"
          ? " rotate(8deg) translateY(0px)"
          : props.type === "fear"
          ? " rotate(8deg) translateY(-3px)"
          : "translateY(-3px)"};
    }
  }
`;

const HappyMouth = styled.div`
  display: inline-flex;
  position: absolute;
  background: white;
  top: inherit;
  bottom: 5px;
  width: 50%;
  height: 6px;
  left: 8px;
  transition: 0.3s ease;
  border-radius: 0 0 50% 50%;
  transform: ${props =>
    props.type === "sad"
      ? "rotateX(-50deg) rotateZ(180deg)"
      : "rotateX(-50deg)"};
  ${SentimentalItem}:hover & {
    transform: ${props =>
      props.type === "sad"
        ? "rotateX(-25deg) rotateZ(180deg)"
        : "rotateX(0deg)"};
  }
`;

const BodyEmoji = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 32px;
  height: 32px;
  background: currentColor;
  border-radius: 5px;
  position: relative;
  opacity: ${props => (props.isActive ? 1 : 0.5)};
  transition: 0.3s ease;
  ${SentimentalItem}:hover & {
    opacity: 1;
  }
`;

const SentimentalExplained = styled.p`
  width: 100%;
  margin-top: 20px;
  text-align: justify;
`;

const ProcessingMessage = styled.div`
  background: #fbfbfb;
  border: 1px dashed #e8e8e8;
  margin: 30px auto;
  padding: 20px 30px;
  max-width: 420px;
  text-align: center;
`;

const SentimentalAnalysis = ({ sentiment }) => {
  const { t } = useTranslation();
  let highestSentiment = "";
  const matchSentiment = (num, key, obj) => {
    // analyse max value of obj and take key and value from result
    const matched = val => R.reduce(R.max, 0, R.valuesIn(obj)) === val;
    highestSentiment = R.replace(
      "Percent",
      "",
      String(R.keys(R.pickBy(matched, obj)))
    );
  };
  const sentimentList = R.pick(
    [
      "neutralPercent",
      "happyPercent",
      "sadPercent",
      "fearPercent",
      "angryPercent",
      "surprisePercent",
      "disgustPercent"
    ],
    sentiment
  );
  R.mapObjIndexed(matchSentiment, sentimentList);

  const sentimental = sentiment && {
    labels: [
      t(`opportunity:process.candidateDetails.sentiments.neutral`),
      t(`opportunity:process.candidateDetails.sentiments.happy`),
      t(`opportunity:process.candidateDetails.sentiments.sad`),
      t(`opportunity:process.candidateDetails.sentiments.fear`),
      t(`opportunity:process.candidateDetails.sentiments.angry`),
      t(`opportunity:process.candidateDetails.sentiments.surprise`),
      t(`opportunity:process.candidateDetails.sentiments.disgust`)
    ],
    datasets: [
      {
        data: [
          sentiment.neutralPercent,
          sentiment.happyPercent,
          sentiment.sadPercent,
          sentiment.fearPercent,
          sentiment.angryPercent,
          sentiment.surprisePercent,
          sentiment.disgustPercent
        ],
        backgroundColor: [
          EmojiBackground.neutral,
          EmojiBackground.happy,
          EmojiBackground.sad,
          EmojiBackground.fear,
          EmojiBackground.angry,
          EmojiBackground.surprise,
          EmojiBackground.disgust
        ]
      }
    ]
  };
  return (
    <SentimentalContainer>
      {Object.keys(sentimentList).length > 0 ? (
        <>
          <ChartContainer>
            <Doughnut
              data={sentimental}
              options={{ legend: { display: false } }}
            />
          </ChartContainer>
          <SentimentalList>
            {Object.keys(sentimentList).map(key => {
              const emojiType = R.replace("Percent", "", key);
              return (
                <SentimentalItem
                  isActive={emojiType === highestSentiment}
                  type={emojiType}
                >
                  <EmojiContainer>
                    <BodyEmoji isActive={emojiType === highestSentiment}>
                      <Eye
                        className={emojiType === highestSentiment && "isActive"}
                        type={emojiType}
                      />
                      <Eye
                        className={emojiType === highestSentiment && "isActive"}
                        type={emojiType}
                      />
                      {emojiType === "angry" ? (
                        <AngryEyeBrow
                          className={
                            emojiType === highestSentiment && "isActive"
                          }
                        />
                      ) : (
                        <EyeBrow
                          className={
                            emojiType === highestSentiment && "isActive"
                          }
                          type={emojiType}
                        />
                      )}

                      {emojiType === "happy" || emojiType === "sad" ? (
                        <HappyMouth
                          className={
                            emojiType === highestSentiment && "isActive"
                          }
                          type={emojiType}
                        />
                      ) : (
                        <Mouth
                          className={
                            emojiType === highestSentiment && "isActive"
                          }
                          type={emojiType}
                        />
                      )}
                    </BodyEmoji>
                  </EmojiContainer>
                  <SentimentalState>
                    {t(
                      `opportunity:process.candidateDetails.sentiments.${emojiType}`
                    )}
                  </SentimentalState>
                  <Percentage>{sentimentList[key]}%</Percentage>
                </SentimentalItem>
              );
            })}
          </SentimentalList>
        </>
      ) : (
        <ProcessingMessage>
          {t("opportunity:process.candidateDetails.stillProcess")}
        </ProcessingMessage>
      )}

      <SentimentalExplained>
        {t("opportunity:process.candidateDetails.sentimentalExplained")}
      </SentimentalExplained>
    </SentimentalContainer>
  );
};

export default SentimentalAnalysis;
